/**
 * Process detail → Items tab. Fetches `/processes/:id/items` (grouped by kind)
 * and renders up to three sections, matching the legacy warehouse render:
 * Equipment (blue) · Components (purple) · Items to Import (green, pending
 * creation on approval). Each equipment/component section is a nested
 * category → name → line-item table; import items expand to a detail grid.
 */
import { Package, Cpu, PackagePlus } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, Chip } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useProcessItems } from '@/api/capacities';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { ProcessItemsGroupedTable } from './process-items-grouped-table';
import { ProcessImportItemsTable } from './process-import-items-table';

/** Section icon colors — semantic solid tokens (theme-reactive). */
const BLUE = 'var(--vn-primary-solid)';
const PURPLE = 'var(--vn-accent-solid)';
const GREEN = 'var(--vn-success-solid)';

function SectionHead({
  icon,
  title,
  badge,
}: {
  icon: React.ReactNode;
  title: string;
  badge: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--vn-space-2)',
        marginBottom: 'var(--vn-space-3)',
      }}
    >
      {icon}
      <h3
        style={{
          margin: 0,
          fontSize: 'var(--vn-font-size-sm)',
          fontWeight: 'var(--vn-font-weight-semibold)',
        }}
      >
        {title}
      </h3>
      <span style={{ marginLeft: 'auto' }}>{badge}</span>
    </div>
  );
}

export function ProcessItemsTab({ processId }: { processId: string }) {
  const { data, isLoading, isError, refetch } = useProcessItems(processId);

  if (isError) return <TabError title="Failed to load items" onRetry={() => refetch()} />;
  if (isLoading || !data) return <TabLoading rows={4} />;

  const equipment = data.equipment ?? [];
  const components = data.components ?? [];
  const importItems = data.import_items ?? [];
  const hasItems = equipment.length > 0 || components.length > 0 || importItems.length > 0;

  return (
    <Card flat style={{ marginTop: 'var(--vn-space-2)' }}>
      <CardHeader>
        <CardTitle>Process Items</CardTitle>
      </CardHeader>
      <CardBody>
        {!hasItems ? (
          <EmptyState
            icon={<Package size={24} />}
            title="No items"
            description="No items associated with this process."
          />
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-6)' }}>
            {equipment.length > 0 && (
              <div>
                <SectionHead
                  icon={<Package size={16} style={{ color: BLUE }} />}
                  title="Equipment"
                  badge={<Chip tone="neutral">{equipment.length}</Chip>}
                />
                <ProcessItemsGroupedTable items={equipment} itemType="equipment" />
              </div>
            )}

            {components.length > 0 && (
              <div>
                <SectionHead
                  icon={<Cpu size={16} style={{ color: PURPLE }} />}
                  title="Components"
                  badge={<Chip tone="neutral">{components.length}</Chip>}
                />
                <ProcessItemsGroupedTable items={components} itemType="component" />
              </div>
            )}

            {importItems.length > 0 && (
              <div>
                <SectionHead
                  icon={<PackagePlus size={16} style={{ color: GREEN }} />}
                  title="Items to Import"
                  badge={<Chip tone="warning">{importItems.length} pending</Chip>}
                />
                <div
                  style={{
                    fontSize: 'var(--vn-font-size-xs)',
                    color: 'var(--vn-fg-muted)',
                    marginBottom: 'var(--vn-space-2)',
                  }}
                >
                  These items will be created in the system when this process is approved. Click a
                  row to view details.
                </div>
                <ProcessImportItemsTable items={importItems} />
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
